// src/components/Gallery.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { austriaReviews, slovakiaReviews } from '../constants/reviews';

const Section = styled.section`
    padding: 100px 0;
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 50%, #f0f4f8 100%);
    position: relative;
`;

const Container = styled.div`
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 3rem;
    position: relative;
    z-index: 2;

    @media (max-width: 768px) {
        padding: 0 1.5rem;
    }
`;

const SectionHeader = styled.div`
    text-align: center;
    margin-bottom: 4rem;
`;

const SectionTitle = styled(motion.h2)`
    font-size: 3.5rem;
    font-weight: 800;
    margin-bottom: 1.5rem;
    background: linear-gradient(135deg, #1a1a1a 0%, #667eea 50%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1.1;

    @media (max-width: 768px) {
        font-size: 2.5rem;
    }
`;

const SectionSubtitle = styled(motion.p)`
    font-size: 1.3rem;
    color: #4a5568;
    line-height: 1.7;
    max-width: 600px;
    margin: 0 auto;
`;

const GalleryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
    margin-bottom: 3rem;
`;

const GalleryItem = styled(motion.div)<{ rowSpan?: number; colSpan?: number }>`
    position: relative;
    border-radius: 16px;
    overflow: hidden;
    cursor: pointer;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;

    /* Dynamic sizing for messy layout */
    grid-row: ${props => props.rowSpan ? `span ${props.rowSpan}` : 'span 1'};
    grid-column: ${props => props.colSpan ? `span ${props.colSpan}` : 'span 1'};

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
    }

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
        opacity: 0;
        transition: opacity 0.3s ease;
        z-index: 1;
    }

    &:hover::before {
        opacity: 1;
    }
`;

const GalleryImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.3s ease;

    ${GalleryItem}:hover & {
        transform: scale(1.05);
    }
`;

const ImageOverlay = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
    padding: 2rem 1.5rem 1.5rem;
    color: white;
    transform: translateY(100%);
    transition: transform 0.3s ease;

    ${GalleryItem}:hover & {
        transform: translateY(0);
    }
`;

const ImageLocation = styled.h4`
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const ImageDescription = styled.p`
    font-size: 0.9rem;
    opacity: 0.9;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
    margin: 0;
`;

// Fixed Modal Styles
const ModalBackdrop = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.95);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
    box-sizing: border-box;

    @media (max-width: 768px) {
        padding: 0.5rem;
    }
`;

const ModalContent = styled(motion.div)`
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 90vw;
    max-height: 90vh;

    @media (max-width: 768px) {
        max-width: 95vw;
        max-height: 95vh;
    }
`;

const ModalImage = styled.img`
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);

    @media (max-width: 768px) {
        border-radius: 8px;
    }
`;

const CloseButton = styled.button`
    position: fixed;
    top: 20px;
    right: 20px;
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    font-size: 1.5rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
    z-index: 1002;

    &:hover {
        background: rgba(255, 255, 255, 0.3);
        transform: scale(1.1);
    }

    @media (max-width: 768px) {
        top: 10px;
        right: 10px;
        width: 40px;
        height: 40px;
        font-size: 1.25rem;
    }
`;

const NavButton = styled.button<{ direction: 'left' | 'right' }>`
    position: fixed;
    top: 50%;
    ${props => props.direction === 'left' ? 'left: 20px;' : 'right: 20px;'}
    transform: translateY(-50%);
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    font-size: 1.5rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
    z-index: 1002;

    &:hover {
        background: rgba(255, 255, 255, 0.3);
        transform: translateY(-50%) scale(1.1);
    }

    @media (max-width: 768px) {
        ${props => props.direction === 'left' ? 'left: 10px;' : 'right: 10px;'}
        width: 40px;
        height: 40px;
        font-size: 1.25rem;
    }

    @media (max-width: 480px) {
        ${props => props.direction === 'left' ? 'left: 5px;' : 'right: 5px;'}
        width: 35px;
        height: 35px;
        font-size: 1.1rem;
    }
`;

const ImageCounter = styled.div`
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    color: white;
    font-size: 1rem;
    background: rgba(255, 255, 255, 0.2);
    padding: 0.5rem 1rem;
    border-radius: 20px;
    backdrop-filter: blur(10px);
    z-index: 1002;

    @media (max-width: 768px) {
        bottom: 10px;
        font-size: 0.9rem;
        padding: 0.4rem 0.8rem;
    }
`;

const FloatingElements = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
`;

const FloatingElement = styled(motion.div)`
    position: absolute;
    background: linear-gradient(135deg, #667eea, #764ba2);
    border-radius: 15px;
    opacity: 0.1;

    &.element-1 {
        width: 120px;
        height: 120px;
        top: 10%;
        right: 5%;
    }

    &.element-2 {
        width: 80px;
        height: 80px;
        bottom: 20%;
        left: 3%;
    }
`;

const Gallery: React.FC = () => {
    const [selectedImage, setSelectedImage] = useState<number | null>(null);

    // Original tour photos (varied sizes for the messy masonry layout)
    const whatsappPhotos = [
        { src: '/WhatsApp Image 2025-10-21 at 10.10.35.jpeg', location: 'Wachau Valley / Durstein', description: '', rowSpan: 1, colSpan: 1 },
        { src: '/WhatsApp Image 2025-10-21 at 10.10.36.jpeg', location: 'Durstein', description: '', rowSpan: 2, colSpan: 1 },
        { src: '/WhatsApp Image 2025-10-21 at 10.10.36 (1).jpeg', location: 'Traunsee', description: '', rowSpan: 1, colSpan: 1 },
        { src: '/WhatsApp Image 2025-10-21 at 10.10.36 (2).jpeg', location: 'Traunkirchen', description: '', rowSpan: 1, colSpan: 2 },
        { src: '/WhatsApp Image 2025-10-21 at 10.10.36 (3).jpeg', location: 'Traunkirchen', description: '', rowSpan: 2, colSpan: 1 },
        { src: '/WhatsApp Image 2025-10-21 at 10.10.36 (4).jpeg', location: 'Traunkirchen', description: '', rowSpan: 1, colSpan: 1 },
        { src: '/WhatsApp Image 2025-10-21 at 10.10.36 (5).jpeg', location: 'Traunkirchen', description: '', rowSpan: 1, colSpan: 1 },
        { src: '/WhatsApp Image 2025-10-21 at 10.10.36 (6).jpeg', location: 'Traunkirchen', description: '', rowSpan: 2, colSpan: 1 },
        { src: '/WhatsApp Image 2025-10-21 at 10.10.37.jpeg', location: 'Traunsee', description: '', rowSpan: 1, colSpan: 1 },
        { src: '/WhatsApp Image 2025-10-21 at 10.10.37 (1).jpeg', location: 'Salzburg Mirabel Garden', description: '', rowSpan: 1, colSpan: 2 },
        { src: '/WhatsApp Image 2025-10-21 at 10.10.37 (2).jpeg', location: 'Salzburg Mirabel Garden', description: '', rowSpan: 2, colSpan: 1 },
        { src: '/smolenice-castle.png', location: 'Smolenice Castle', description: '', rowSpan: 2, colSpan: 1 },
        { src: '/WhatsApp Image 2025-10-21 at 10.12.51 (2).jpeg', location: 'Trenčín', description: '', rowSpan: 2, colSpan: 1 },
        { src: '/WhatsApp Image 2025-10-21 at 10.12.51 (3).jpeg', location: 'Trenčín', description: '', rowSpan: 1, colSpan: 1 } //15
    ];

    // Customer photos pulled from the real reviews data (deduped) so the gallery
    // stays current as new reviews land instead of drifting out of date.
    const reviewSeen = new Set<string>();
    const reviewPhotos = [
        ...austriaReviews.map((review) => ({ review, tour: 'Austria Tour' })),
        ...slovakiaReviews.map((review) => ({ review, tour: 'Slovakia Tour' })),
    ]
        .sort((a, b) => new Date(b.review.date).getTime() - new Date(a.review.date).getTime())
        .flatMap(({ review, tour }) =>
        (review.images ?? [])
            .filter((src) => !reviewSeen.has(src) && reviewSeen.add(src))
            .map((src, i) => ({
                src,
                location: tour,
                description: `Guest photo — ${review.name}`,
                rowSpan: i % 5 === 0 ? 2 : 1,
                colSpan: 1,
            }))
    );

    // Newest customer photos first (sorted by review date above), then the original tour set.
    const galleryImages = [...reviewPhotos, ...whatsappPhotos];

    const openModal = (index: number) => {
        setSelectedImage(index);
        // Prevent body scroll when modal is open
        document.body.style.overflow = 'hidden';
    };

    const closeModal = () => {
        setSelectedImage(null);
        // Restore body scroll
        document.body.style.overflow = 'unset';
    };

    const goToNext = () => {
        if (selectedImage !== null) {
            setSelectedImage((selectedImage + 1) % galleryImages.length);
        }
    };

    const goToPrev = () => {
        if (selectedImage !== null) {
            setSelectedImage((selectedImage - 1 + galleryImages.length) % galleryImages.length);
        }
    };

    // Handle keyboard navigation
    React.useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (selectedImage !== null) {
                if (e.key === 'Escape') closeModal();
                if (e.key === 'ArrowRight') goToNext();
                if (e.key === 'ArrowLeft') goToPrev();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            // Ensure body scroll is restored when component unmounts
            document.body.style.overflow = 'unset';
        };
    }, [selectedImage]);

    return (
        <Section id="gallery">
            <FloatingElements>
                <FloatingElement
                    className="element-1"
                    animate={{
                        y: [0, -40, 0],
                        rotate: [0, 180, 360]
                    }}
                    transition={{
                        duration: 8,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                />
                <FloatingElement
                    className="element-2"
                    animate={{
                        y: [0, 25, 0],
                        rotate: [0, -90, 0]
                    }}
                    transition={{
                        duration: 6,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1
                    }}
                />
            </FloatingElements>

            <Container>
                <SectionHeader>
                    <SectionTitle
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        Our Travel Gallery
                    </SectionTitle>
                    <SectionSubtitle
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        viewport={{ once: true }}
                    >
                        Explore the breathtaking destinations and unforgettable moments from our past tours.
                        This is what awaits you on your Central European adventure with us.
                    </SectionSubtitle>
                </SectionHeader>

                <GalleryGrid>
                    {galleryImages.map((image, index) => (
                        <GalleryItem
                            key={index}
                            rowSpan={image.rowSpan}
                            colSpan={image.colSpan}
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                            viewport={{ once: true }}
                            whileHover={{ scale: 1.02 }}
                            onClick={() => openModal(index)}
                        >
                            <GalleryImage
                                src={image.src}
                                alt={`${image.location} - ${image.description}`}
                                loading="lazy"
                                onError={(e) => {
                                    // Fallback to placeholder if image doesn't exist
                                    e.currentTarget.src = `https://picsum.photos/400/300?random=${index}`;
                                }}
                            />
                            <ImageOverlay>
                                <ImageLocation>{image.location}</ImageLocation>
                                <ImageDescription>{image.description}</ImageDescription>
                            </ImageOverlay>
                        </GalleryItem>
                    ))}
                </GalleryGrid>

                {/* Modal for image viewing */}
                <AnimatePresence>
                    {selectedImage !== null && (
                        <ModalBackdrop
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={closeModal}
                        >
                            <ModalContent
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.8, opacity: 0 }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <ModalImage
                                    src={galleryImages[selectedImage].src}
                                    alt={galleryImages[selectedImage].location}
                                    onError={(e) => {
                                        e.currentTarget.src = `https://picsum.photos/800/600?random=${selectedImage}`;
                                    }}
                                />
                            </ModalContent>

                            {/* Modal controls - placed outside ModalContent for better positioning */}
                            <CloseButton onClick={closeModal}>×</CloseButton>
                            <NavButton direction="left" onClick={goToPrev}>‹</NavButton>
                            <NavButton direction="right" onClick={goToNext}>›</NavButton>
                            <ImageCounter>
                                {selectedImage + 1} / {galleryImages.length}
                            </ImageCounter>
                        </ModalBackdrop>
                    )}
                </AnimatePresence>
            </Container>
        </Section>
    );
};

export default Gallery;